import { Base } from '@studiometa/js-toolkit';

const KLAVIYO_API_REVISION = '2026-01-15';

/**
 * Klaviyo Notify Me — Back In Stock modal.
 *
 * Deux points d'entrée :
 *   1. Clic sur [data-klaviyo-notify-trigger] dans buy-button.liquid (délégation sur document)
 *   2. Événement window `klaviyoNotifyMeOpen` dispatché par VariantSelects.js
 *
 * Flux soumission :
 *   1. POST /client/back-in-stock-subscriptions — crée la subscription BIS + le profil Klaviyo
 *   2. POST /client/profiles — met à jour la propriété `language` sur le profil
 */
export default class KlaviyoNotifyMe extends Base {
  static config = {
    name: 'KlaviyoNotifyMe',
    refs: [
      'overlay',
      'container',
      'closeBtn',
      'emailInput',
      'submitBtn',
      'submitText',
      'variantTitle',
      'formState',
      'successState',
      'errorMessage',
    ],
  };

  // Stocke l'ID du variant courant entre l'ouverture et la soumission
  _variantId = null;

  /**
   * mounted — attache un listener délégué sur document pour les boutons trigger.
   * On utilise la délégation car buy-button.liquid est re-rendu par VariantSelects
   * à chaque changement de variant (via renderProductInfo → #main-add-to-cart).
   */
  mounted() {
    this._onDocumentClick = (e) => {
      const trigger = e.target.closest('[data-klaviyo-notify-trigger]');
      if (trigger) {
        this.open({
          variantId: trigger.dataset.variantId,
          variantTitle: trigger.dataset.variantTitle,
          productTitle: trigger.dataset.productTitle,
        });
      }
    };
    document.addEventListener('click', this._onDocumentClick);
  }

  /**
   * destroyed — nettoyage du listener pour éviter les fuites mémoire.
   */
  destroyed() {
    document.removeEventListener('click', this._onDocumentClick);
  }

  // ---------------------------------------------------------------------------
  // Événements window
  // ---------------------------------------------------------------------------

  /**
   * Écoute l'événement `klaviyoNotifyMeOpen` dispatché par VariantSelects.js
   * quand l'utilisateur clique sur le bouton "Sold out" désactivé.
   * Studio Meta convertit automatiquement `klaviyoNotifyMeOpen` → `onWindowKlaviyoNotifyMeOpen`.
   * @param {object} props
   * @param {CustomEvent} props.event
   */
  onWindowKlaviyoNotifyMeOpen({ event }) {
    this.open(event.detail);
  }

  // ---------------------------------------------------------------------------
  // Événements refs
  // ---------------------------------------------------------------------------

  /**
   * Ferme la modale au clic sur l'overlay.
   */
  onOverlayClick() {
    this.close();
  }

  /**
   * Ferme la modale au clic sur le bouton ×.
   */
  onCloseBtnClick() {
    this.close();
  }

  /**
   * Lance la soumission au clic sur le bouton submit.
   */
  onSubmitBtnClick() {
    this.submit();
  }

  /**
   * Soumet également en appuyant sur Entrée dans l'input email.
   * @param {KeyboardEvent} e
   */
  onEmailInputKeydown({ event }) {
    if (event.key === 'Enter') {
      this.submit();
    }
  }

  // ---------------------------------------------------------------------------
  // Méthodes publiques
  // ---------------------------------------------------------------------------

  /**
   * Ouvre la modale et initialise son état avec les données du variant.
   * @param {object} detail
   * @param {string|number} detail.variantId   — ID Shopify du variant
   * @param {string}        detail.variantTitle — Titre du variant (ex: "Rose - 50ml")
   * @param {string}        detail.productTitle — Titre produit (fallback si pas de variantTitle)
   */
  open({ variantId, variantTitle, productTitle } = {}) {
    this._variantId = variantId !== null && variantId !== undefined ? String(variantId) : null;

    // Affiche le titre du variant dans la modale pour confirmation visuelle
    this.$refs.variantTitle.textContent = variantTitle || productTitle || '';

    // Réinitialise l'état (utile si la modale a déjà été utilisée)
    this.$refs.formState.classList.remove('hidden');
    this.$refs.successState.classList.add('hidden');
    this.$refs.errorMessage.classList.add('hidden');
    this.$refs.emailInput.value = '';
    this.$refs.submitBtn.disabled = false;

    // Affiche la modale
    this.$el.classList.remove('opacity-0', 'pointer-events-none');
    this.$el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('overflow-hidden');

    // Focus sur l'input après la transition CSS (300ms)
    setTimeout(() => this.$refs.emailInput.focus(), 320);
  }

  /**
   * Ferme la modale.
   */
  close() {
    this.$el.classList.add('opacity-0', 'pointer-events-none');
    this.$el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('overflow-hidden');
  }

  // ---------------------------------------------------------------------------
  // Méthodes privées
  // ---------------------------------------------------------------------------

  /**
   * Gère la soumission du formulaire.
   * Enchaîne les deux appels API Klaviyo de façon séquentielle.
   */
  async submit() {
    const email = this.$refs.emailInput.value.trim();

    if (!email || !this._variantId) return;

    this.setLoading(true);
    this.$refs.errorMessage.classList.add('hidden');

    try {
      // Étape 1 — Inscription BIS : crée la subscription + le profil Klaviyo
      await this.subscribeBackInStock(email, this._variantId);

      // Étape 2 — Mise à jour de la langue sur le profil
      // Fait après l'étape 1 pour être sûr que le profil existe
      await this.updateProfileLanguage(email);

      // Succès : affiche le message de confirmation
      this.$refs.formState.classList.add('hidden');
      this.$refs.successState.classList.remove('hidden');
    } catch (err) {
      console.error('[KlaviyoNotifyMe]', err);
      this.$refs.errorMessage.classList.remove('hidden');
    } finally {
      this.setLoading(false);
    }
  }

  /**
   * Active/désactive l'état de chargement sur le bouton submit.
   * Le texte du bouton est masqué et le spinner affiché pendant le chargement.
   * @param {boolean} isLoading
   */
  setLoading(isLoading) {
    this.$refs.submitBtn.disabled = isLoading;
    this.$refs.submitText.classList.toggle('invisible', isLoading);
    const spinner = this.$refs.submitBtn.querySelector('.loading__spinner');
    if (spinner) spinner.classList.toggle('hidden', !isLoading);
  }

  /**
   * Appel 1 — Crée la subscription Back in Stock pour le variant et l'email donnés.
   * Klaviyo crée automatiquement le profil s'il n'existe pas, puis surveille le
   * stock via l'intégration Shopify native et déclenche le flow BIS.
   * @param {string} email
   * @param {string} variantId — ID Shopify numérique (sans préfixe)
   */
  async subscribeBackInStock(email, variantId) {
    const res = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${window.klaviyoPublicKey}`,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          revision: KLAVIYO_API_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  // Format attendu par Klaviyo pour les variants Shopify
                  id: `$shopify:::$default:::${variantId}`,
                },
              },
            },
          },
        }),
      },
    );

    if (!res.ok) {
      throw new Error(`[Klaviyo BIS] HTTP ${res.status}`);
    }
  }

  /**
   * Appel 2 — Met à jour la propriété `language` sur le profil Klaviyo.
   * Utilisée dans le flow BIS pour le Conditional Split FR/EN.
   *
   * La langue est lue depuis `window.Shopify.locale` (ex: "fr", "en") qui reflète
   * la langue choisie par l'utilisateur sur le storefront — plus fiable que
   * `navigator.language` qui dépend du navigateur.
   * @param {string} email
   */
  async updateProfileLanguage(email) {
    // Shopify.locale renvoie "fr" ou "en" — on prend la partie avant "-" par sécurité
    const language = (window.Shopify?.locale ?? 'en').split('-')[0];

    const res = await fetch(
      `https://a.klaviyo.com/client/profiles/?company_id=${window.klaviyoPublicKey}`,
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          revision: KLAVIYO_API_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: 'profile',
            attributes: {
              email,
              properties: { language },
            },
          },
        }),
      },
    );

    // 409 Conflict = profil déjà existant, Klaviyo fusionne les données → OK
    if (!res.ok && res.status !== 409) {
      throw new Error(`[Klaviyo Profile] HTTP ${res.status}`);
    }
  }
}
